import {
  Button,
  ButtonLink,
  CircleQuestionIcon,
  FileUploadField,
  FileUploadFieldController,
  RadioFieldController,
  ScheduledDateTimeFieldController,
  Stack,
  TextAreaFieldController,
  TextInputFieldController,
} from '@fukuri-admins/ui/common';
import { ROUTES } from '~/utils/routes';
import styles from './styles.module.scss';

type Props = {
  /** 「作成」ボタンの非活性状態 */
  isSubmitDisabled: boolean;
  /** 公開設定の現在値（'scheduled': 予約投稿 / 'immediately': 今すぐ公開） */
  publishMode: 'scheduled' | 'immediately';
  /** フォーム送信ハンドラ */
  onSubmit: React.ComponentProps<'form'>['onSubmit'];
  /** 添付ファイルの現在値 */
  attachedFiles: File[];
  /** ファイルアップロード中フラグ */
  isFileUploading: boolean;
  /** ファイルアップロード非活性フラグ */
  isFileUploadDisabled: boolean;
  /** ファイル変更ハンドラ */
  onFilesChange: (files: File[]) => void;
};

/**
 * 社内報作成フォームの Presentation コンポーネント
 */
export const CreateInternalNewsPresentation: React.FC<Props> = ({
  isSubmitDisabled,
  publishMode,
  onSubmit,
  attachedFiles,
  isFileUploading,
  isFileUploadDisabled,
  onFilesChange,
}) => (
  <form onSubmit={onSubmit} className={styles.form}>
    <Stack direction='column' gap={24}>
      <div className={styles.titleRow}>
        <p className={styles.formTitle}>社内報の新規作成</p>
        <a
          href={ROUTES.COMPANY_NEWSLETTER_GUIDE}
          target='_blank'
          rel='noopener noreferrer'
          className={styles.guideLink}
          aria-label='ガイドを確認'
        >
          <CircleQuestionIcon size='md' className={styles.guideLinkIcon} aria-hidden />
        </a>
      </div>

      <div className={styles.formContainer}>
        <Stack direction='column' gap={24}>
          <TextInputFieldController
            name='title'
            fieldWrapperProps={{
              labelType: 'required',
              labelText: 'タイトル',
            }}
            textInput={{
              type: 'text',
              placeholder: 'タイトルを入力してください',
            }}
          />

          <TextAreaFieldController
            name='body'
            fieldWrapperProps={{
              labelType: 'required',
              labelText: '本文',
              // URLを入力すると自動的にリンクになる旨をユーザーに案内する
              beforeMessage: 'URLを入力すると、自動的にクリックできるリンクになります。',
            }}
            textArea={{
              placeholder: '社内報の本文を入力',
            }}
          />

          <Stack direction='column' gap={8}>
            <RadioFieldController
              name='publishMode'
              fieldWrapperProps={{
                labelType: 'required',
                labelText: '公開設定',
              }}
              radioGroup={{
                options: [
                  { value: 'immediately', label: '今すぐ公開する' },
                  { value: 'scheduled', label: '予約投稿' },
                ],
                direction: 'column',
                gap: 8,
              }}
            />

            <div className={styles.scheduledDateTimeWrapper}>
              <ScheduledDateTimeFieldController disabled={publishMode !== 'scheduled'} />
            </div>
          </Stack>

          <FileUploadFieldController
            name='mainImage'
            fileUpload={{
              type: 'image',
              accept: ['jpg', 'jpeg', 'png', 'gif', 'heic'],
              maxSizeMb: 10,
            }}
            fieldWrapperProps={{
              labelText: 'メインビジュアル',
              beforeMessage: '記事の上部に表示されるメイン画像',
            }}
          />

          <FileUploadField
            fileUpload={{
              type: 'file',
              isMultiple: true,
              accept: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'jpg', 'jpeg', 'png'],
              maxSizeMb: 10,
              value: attachedFiles,
              onChange: onFilesChange,
              isUploading: isFileUploading,
              disabled: isFileUploadDisabled,
            }}
            fieldWrapperProps={{
              labelText: 'ファイル',
              beforeMessage: 'PDFと画像のアップロードが可能。ファイルサイズは10MBまで。',
            }}
          />
        </Stack>
      </div>

      <Stack direction='row' gap={8}>
        <ButtonLink to={ROUTES.INTERNAL_NEWS.INDEX} variant='outline' size='md'>
          キャンセル
        </ButtonLink>
        <Button type='submit' size='md' disabled={isSubmitDisabled}>
          作成
        </Button>
      </Stack>
    </Stack>
  </form>
);
